import { FC } from "react"
import { Alert, Col, Form, FormInstance, InputNumber, Row, Select, Switch } from "antd"
import { GAMEPLAY_CUT_CLASSES, GameplayCutClass, GameplayCutProfile } from "../store/types"
import {
  ALWAYS_CUT_CLASSES,
  GAMEPLAY_CUT_CLASS_LABELS,
  GAMEPLAY_CUT_OUTPUT_OPTIONS,
  GAMEPLAY_CUT_PRESETS,
  GAMEPLAY_CUT_PROFILE_OPTIONS,
  GameplayCutFormValues,
  MAX_CUT_SECONDS,
  cutClassesWithoutDetector,
} from "../gameplay-cut"

export const GameplayCutFields: FC<{ form: FormInstance<GameplayCutFormValues> }> = ({ form }) => {
  const keep: Record<GameplayCutClass, boolean> | undefined = Form.useWatch("keep", form)
  const undetected = keep ? cutClassesWithoutDetector(keep) : []

  const onProfileChange = (profile: GameplayCutProfile) => {
    const { keep: presetKeep, shortBreakSec } = GAMEPLAY_CUT_PRESETS[profile]
    form.setFieldsValue({ keep: { ...presetKeep }, shortBreakSec })
  }

  return (
    <>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <Form.Item name="profile" label="Profile">
            <Select options={GAMEPLAY_CUT_PROFILE_OPTIONS} onChange={onProfileChange} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="outputMode" label="Output mode">
            <Select options={GAMEPLAY_CUT_OUTPUT_OPTIONS} />
          </Form.Item>
        </Col>
      </Row>

      <div style={{ fontWeight: 600, margin: "0 0 4px" }}>Keep</div>
      <Row gutter={[16, 0]}>
        {GAMEPLAY_CUT_CLASSES.map(c => (
          <Col span={4} key={c}>
            <Form.Item
              name={["keep", c]}
              label={GAMEPLAY_CUT_CLASS_LABELS[c]}
              valuePropName="checked"
              extra={ALWAYS_CUT_CLASSES.includes(c) ? "Always cut." : undefined}
            >
              <Switch aria-label={`Keep ${GAMEPLAY_CUT_CLASS_LABELS[c]}`} disabled={ALWAYS_CUT_CLASSES.includes(c)} />
            </Form.Item>
          </Col>
        ))}
      </Row>
      {undetected.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${undetected.map(c => GAMEPLAY_CUT_CLASS_LABELS[c]).join(", ")}: no detector yet — reported as unavailable, kept`}
        />
      )}

      <Row gutter={[16, 0]}>
        <Col span={4}>
          <Form.Item name="idleThresholdSec" label="Idle threshold (s)" rules={[{ required: true }]}>
            <InputNumber min={1} max={MAX_CUT_SECONDS} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="shortBreakSec" label="Short break (s)" extra="Blank: not applied.">
            <InputNumber min={0} max={MAX_CUT_SECONDS} precision={0} style={{ width: "100%" }} placeholder="off" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="minBlockSec" label="Min block (s)" rules={[{ required: true }]}>
            <InputNumber min={0} max={MAX_CUT_SECONDS} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="coplayMinSec" label="Co-play min (s)" rules={[{ required: true }]}>
            <InputNumber min={0} max={MAX_CUT_SECONDS} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="coplayMinPlayers" label="Co-play min players" rules={[{ required: true }]}>
            <InputNumber min={2} max={16} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}
